import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'motion/react';
import { ArrowUpRight, Calendar, Clock } from 'lucide-react';
import { RevealSection, RevealCard, staggerContainer } from '../components/Animations';

import { blogPosts } from '../data/blogData';
import SEO from '../components/SEO';

const BlogPage = () => {
  const blogSchema = {
    '@context': 'https://schema.org',
    '@type': 'Blog',
    name: 'Emerge Automations AI Insights',
    description: 'Expert insights on AI automation, LLMs, and strategic AI integration for SMEs.',
    publisher: {
      '@type': 'Organization',
      name: 'Emerge Automations',
      logo: {
        '@type': 'ImageObject',
        url: 'https://emergeautomations.com/logo.png',
      },
    },
    blogPost: blogPosts.map((post) => ({
      '@type': 'BlogPosting',
      headline: post.title,
      description: post.excerpt,
      datePublished: post.date,
      image: post.image,
      url: `https://emergeautomations.com/blog/${post.id}`,
    })),
  };

  const featured = blogPosts[0];
  const rest = blogPosts.slice(1);

  return (
    <div className="relative">
      <SEO
        title="AI Automation Insights & Guides | Emerge Automations"
        description="Stay ahead of the AI curve with expert insights on automation, LLMs, and strategic AI integration for SMEs in the UK and USA."
        canonical="https://emergeautomations.com/blog/"
        schemaData={blogSchema}
        keywords="AI automation blog, AI business insights, workflow automation guides, AI voice agents tips, n8n tutorials, SME AI strategy, UK AI automation, USA AI automation, future of AI 2026"
        breadcrumbs={[
          { name: 'Home', item: 'https://emergeautomations.com' },
          { name: 'Blog', item: 'https://emergeautomations.com/blog' },
        ]}
      />

      <div className="pt-32 md:pt-40 pb-24">
        <div className="container-custom">
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: 0.5 }}>
            {/* Header */}
            <div className="mb-14 md:mb-20">
              <RevealSection>
                <span className="eyebrow">Insights</span>
                <div className="mt-3 mb-8 h-px bg-white/10" />
                <h1 className="text-[clamp(2.5rem,6vw,4.5rem)] font-display font-semibold text-white leading-[1.02] tracking-[-0.035em] max-w-4xl">
                  Field notes on AI automation
                </h1>
              </RevealSection>
              <RevealSection>
                <div className="mt-10 max-w-3xl space-y-5 text-[17px] text-zinc-400 leading-[1.7]">
                  <p>
                    This is where we share what we are learning as we build AI automation for small and medium businesses across the UK and United States. The posts here cover the practical side of the technology: how AI voice agents actually handle phone calls, when n8n workflows save more time than Zapier, how to fine-tune a custom language model on your own company data, and what a real AI readiness audit looks like before you commit budget to a project.
                  </p>
                  <p>
                    Our readers are owners, operators, and operations leads at growing companies. People who want to understand AI well enough to make smart calls about where to invest, but do not have time to wade through a thousand-word press release every week. We write for that audience: plain English, specific numbers, and concrete examples from the work we do for clients.
                  </p>
                  <p>
                    Expect honest breakdowns of tools and tactics. You will see the wins and the ways things go sideways, what setup costs look like in pounds and dollars, which workflows reliably pay for themselves, and which pitches from AI vendors you can safely ignore. Every post is written by the team who actually build these systems, not by a content marketing agency. If you are weighing whether AI automation belongs in your business this year, start with the featured post below.
                  </p>
                </div>
              </RevealSection>
            </div>

            {/* Featured */}
            <RevealSection>
              <Link
                to={`/blog/${featured.id}/`}
                className="group block mb-14"
                aria-label={`Read featured article: ${featured.title}`}
              >
                <RevealCard className="grid lg:grid-cols-12 gap-8 lg:gap-10 p-4 md:p-6 rounded-[var(--radius-xl)] border border-white/10 bg-[color:var(--color-surface-1)] hover:border-white/25 transition-colors overflow-hidden">
                  <div className="lg:col-span-7 aspect-[16/10] rounded-[var(--radius-lg)] overflow-hidden border border-white/5">
                    <img
                      src={featured.image}
                      sizes="(max-width: 768px) 100vw, 58vw"
                      alt={`Featured Insight: ${featured.title} - AI Automation Strategies for 2026`}
                      className="w-full h-full object-cover grayscale-[0.15] group-hover:grayscale-0 group-hover:scale-[1.02] transition-all duration-500"
                      width="800"
                      height="500"
                      referrerPolicy="no-referrer"
                      fetchPriority="high"
                      decoding="async"
                    />
                  </div>
                  <div className="lg:col-span-5 flex flex-col justify-center py-2 md:py-6 pr-2 md:pr-4">
                    <div className="flex items-center gap-4 mb-6">
                      <span className="eyebrow eyebrow--accent">{featured.category}</span>
                      <span className="w-1 h-1 rounded-full bg-zinc-600" />
                      <span className="flex items-center gap-1.5 text-xs text-zinc-500 font-medium">
                        <Calendar className="w-3 h-3" strokeWidth={1.75} />
                        {featured.date}
                      </span>
                    </div>
                    <h2 className="text-2xl md:text-3xl font-display font-semibold text-white leading-[1.15] tracking-[-0.02em] mb-5 group-hover:text-[color:var(--color-accent)] transition-colors">
                      {featured.title}
                    </h2>
                    <p className="text-[15px] text-zinc-400 leading-relaxed mb-8 line-clamp-3">
                      {featured.excerpt}
                    </p>
                    <div className="inline-flex items-center gap-1.5 text-sm font-medium text-white">
                      Read featured article
                      <ArrowUpRight
                        className="w-4 h-4 text-zinc-400 group-hover:text-[color:var(--color-accent)] group-hover:-translate-y-0.5 group-hover:translate-x-0.5 transition-all"
                        strokeWidth={1.75}
                      />
                    </div>
                  </div>
                </RevealCard>
              </Link>
            </RevealSection>

            {/* Grid */}
            <motion.div
              variants={staggerContainer}
              initial="initial"
              whileInView="whileInView"
              viewport={{ once: true, margin: '-100px' }}
              className="grid md:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-5"
            >
              {rest.map((post) => (
                <Link
                  key={post.id}
                  to={`/blog/${post.id}/`}
                  className="group block h-full"
                  aria-label={`Read blog post: ${post.title}`}
                >
                  <RevealCard className="h-full rounded-[var(--radius-lg)] bg-[color:var(--color-surface-1)] border border-white/10 hover:border-white/25 transition-colors overflow-hidden flex flex-col">
                    <div className="aspect-[16/10] overflow-hidden border-b border-white/5">
                      <img
                        src={post.image}
                        alt={`Emerge Automations Insight: ${post.title} - Business AI Implementation`}
                        className="w-full h-full object-cover grayscale-[0.2] group-hover:grayscale-0 group-hover:scale-105 transition-all duration-500"
                        width="400"
                        height="250"
                        referrerPolicy="no-referrer"
                        loading="lazy"
                        decoding="async"
                      />
                    </div>
                    <div className="p-6 flex flex-col flex-grow">
                      <div className="flex items-center justify-between mb-5">
                        <span className="eyebrow eyebrow--accent">{post.category}</span>
                        <span className="flex items-center gap-1.5 text-[11px] text-zinc-500 font-medium">
                          <Clock className="w-3 h-3" strokeWidth={1.75} />
                          {post.readTime}
                        </span>
                      </div>
                      <h3 className="text-[17px] font-semibold text-white leading-[1.3] tracking-[-0.01em] mb-3 line-clamp-2 group-hover:text-[color:var(--color-accent)] transition-colors">
                        {post.title}
                      </h3>
                      <p className="text-sm text-zinc-400 leading-relaxed line-clamp-3 mb-5">
                        {post.excerpt}
                      </p>
                      <div className="mt-auto pt-5 border-t border-white/5 flex items-center justify-between text-sm font-medium text-white">
                        <span>Read more</span>
                        <ArrowUpRight
                          className="w-4 h-4 text-zinc-500 group-hover:text-[color:var(--color-accent)] transition-colors"
                          strokeWidth={1.75}
                        />
                      </div>
                    </div>
                  </RevealCard>
                </Link>
              ))}
            </motion.div>

            {/* Newsletter */}
            <RevealSection className="mt-24 md:mt-32">
              <RevealCard className="relative p-10 md:p-16 rounded-[var(--radius-xl)] border border-white/10 bg-[color:var(--color-surface-1)] overflow-hidden">
                <div
                  className="pointer-events-none absolute inset-0 opacity-60"
                  style={{
                    background:
                      'radial-gradient(circle at 50% 120%, rgba(198,242,78,0.08), transparent 55%)',
                  }}
                />
                <div className="relative grid lg:grid-cols-12 gap-10 items-center">
                  <div className="lg:col-span-7">
                    <span className="eyebrow eyebrow--accent mb-5 inline-block">Newsletter</span>
                    <h2 className="text-3xl md:text-4xl font-display font-semibold text-white leading-[1.1] tracking-[-0.025em] mb-4">
                      Stay ahead of the AI curve
                    </h2>
                    <p className="text-[16px] text-zinc-400 leading-relaxed max-w-lg">
                      Join 2,000+ business leaders receiving our weekly breakdown of the most impactful AI tools and automation strategies.
                    </p>
                  </div>
                  <form className="lg:col-span-5 flex flex-col sm:flex-row gap-2">
                    <input
                      type="email"
                      name="email"
                      autoComplete="email"
                      aria-label="Work email for newsletter"
                      placeholder="Enter your work email"
                      className="flex-grow min-w-0 px-4 py-3.5 rounded-[var(--radius-md)] bg-[color:var(--color-bg-base)] border border-white/10 text-white placeholder:text-zinc-600 text-[15px] focus:outline-none focus:border-[color:var(--color-accent)] transition-colors"
                      required
                    />
                    <button className="btn-primary justify-center min-w-[120px]">
                      Subscribe
                    </button>
                  </form>
                </div>
              </RevealCard>
            </RevealSection>
          </motion.div>
        </div>
      </div>
    </div>
  );
};

export default BlogPage;
